import { formatNamedDate } from '@orn/shared-web/lib/date/localDay';

import { NetWorthExclusionReason } from '../../__generated__/globalTypes';
import { toCurrency } from '../formatting';

import type { HouseholdNetWorthQuery } from '../../hooks/queries/__generated__/useHouseholdNetWorth.gql';

type HouseholdNetWorth = HouseholdNetWorthQuery['householdNetWorth'];

/** The age at which `SyncStatus` turns a row's dot amber; the total's label names its date from the same day. */
export const NET_WORTH_STALE_AFTER_DAYS = 7;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const EXCLUSION_TOOLTIPS: Record<NetWorthExclusionReason, string> = {
  [NetWorthExclusionReason.NoBalance]: 'Not in the total: the bank has not reported a balance for this account.',
  [NetWorthExclusionReason.NonUsd]: 'Not in the total: this balance is not in US dollars.',
};

/**
 * The label beside the household total. It says "as of", never "sync": `asOf` is the oldest
 * time any counted balance was last confirmed current — a bank account's last sync, an
 * investment account's last balance read — so it is never older than the stalest row.
 *
 * @param asOf `householdNetWorth.asOf`; null when the server counted no account.
 */
export function netWorthTotalLabel(asOf: string | null | undefined, now: Date): string {
  if (!asOf) {
    return 'Total';
  }
  const asOfDate = new Date(asOf);
  const ageDays = Math.floor((now.getTime() - asOfDate.getTime()) / MS_PER_DAY);
  if (ageDays < NET_WORTH_STALE_AFTER_DAYS) {
    return 'Total as of today';
  }
  return `Total as of ${formatNamedDate(asOfDate, now)}`;
}

/** The household total as currency, or an em dash when it is unread or the server counted no account. */
export function netWorthTotalText(netWorth: Pick<HouseholdNetWorth, 'total' | 'asOf'> | null | undefined): string {
  if (!netWorth?.asOf) {
    return '—';
  }
  return toCurrency(netWorth.total);
}

/** Each account the server left out of the total, mapped to the sentence that says why. */
export function toExclusionTooltips(excluded: HouseholdNetWorth['excluded'] | undefined): ReadonlyMap<string, string> {
  return new Map((excluded ?? []).map(({ accountId, reason }) => [accountId, EXCLUSION_TOOLTIPS[reason]]));
}
